import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Form } from "react-bootstrap";
import api from "../api";
import ReactGA from "react-ga4";
import pLimit from "p-limit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCamera, faUpload, faSpinner, faSlidersH, faChevronDown, faChevronUp, faCloudUploadAlt, faTimes, faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from 'react-router-dom';
import { useAuth } from "../AuthContext";
import styles from '../components/style/UploadImg.module.css';
import { bytesToSize } from '../utils/bytesSize';
import EventTabNav from "../components/EventTabNav";


const UploadImg = ({ event, setPageName }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [totalUploaded, setTotalUploaded] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [totalSize, setTotalSize] = useState(0);
  const [totalSizeByte, setTotalSizeByte] = useState(0);
  const [alertMessage, setAlertMessage] = useState({success: false, message: ""});
  const { user, events, refreshEvents } = useAuth();
  const [usedStorage, setUsedStorage] = useState(0);
  const [storageOver, setStorageOver] = useState(false);
  const [abortUpload, setAbortUpload] = useState(false);
  const controllerRef = React.useRef(null);
  const totalUploadedRef = React.useRef(0);

  const [previewSize, setPreviewSize] = useState(null);
  const [estimating, setEstimating] = useState(false);
  const [newSize, setNewSize] = useState(user?.default_upload_mode === "original" ? -1 : 1600);
  const [newQuality, setNewQuality] = useState(100);
  const [showManager, setShowManager] = useState(false);
  const [fileProgress, setFileProgress] = useState({});
  const [networkSpeed, setNetworkSpeed] = useState(null);

  const uploadStartRef = React.useRef(null);
  const perFileLoadedRef = React.useRef({});
  const totalLoadedRef = React.useRef(0);

  const formatSpeed = (bps) => {
    if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
    if (bps >= 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
    return `${Math.round(bps)} B/s`;
  };

  const handleEstimateSize = async () => {
    if (selectedFiles.length === 0) return alert("Select at least one image.");
    const file = selectedFiles[0];
    const formData = new FormData();
    formData.append("image", file);
    setEstimating(true);
    try {
      const response = await api.post(
        `/images/estimate_size?eid=${event.event_id}&new_size=${newSize}&new_quality=${newQuality}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const coefNewSize = response.data.size_bytes / file.size;
      const newSizeByte = totalSizeByte * coefNewSize;
      setPreviewSize(bytesToSize(newSizeByte));
      checkStorageLimit(newSizeByte);
    } catch (err) {
      console.error("Error estimating size:", err);
      setPreviewSize(null);
    } finally {
      setEstimating(false);
    }
  };

  useEffect(() => {
    if (selectedFiles.length === 0) return;
    if (newSize === -1 || newSize === 1600) handleEstimateSize();
  }, [newSize]);

  useEffect(() => {
    if (selectedFiles.length > 0) handleEstimateSize();
    setTotalSize(bytesToSize(totalSizeByte));
  }, [totalSizeByte]);

  useEffect(() => {
    setPageName("Upload" + (event ? ` - ${event.name}` : ""));
    const totalUsed = (events ?? []).reduce((acc, ev) => acc + (ev.storage?.used_memory || 0), 0);
    setUsedStorage(totalUsed);
  }, [events, event]);

  useEffect(() => {
    if (selectedFiles.length > 0)
      setUploadProgress(Math.round((totalUploaded / selectedFiles.length) * 100));
  }, [totalUploaded]);

  useEffect(() => {
    const baseName = "Upload" + (event ? ` - ${event.name}` : "");
    if (isUploading && uploadProgress > 0) {
      document.title = `${uploadProgress}% — ${baseName}`;
    } else {
      document.title = baseName;
    }
    return () => { document.title = baseName; };
  }, [isUploading, uploadProgress, event]);

  useEffect(() => {
    setTotalSizeByte(selectedFiles.reduce((acc, file) => acc + file.size, 0));
  }, [selectedFiles]);

  const checkStorageLimit = (totalSizeB) => {
    const memory_limit_bytes = user?.subscription?.memory_limit_gb * 1024 * 1024 * 1024;
    if (totalSizeB + usedStorage > memory_limit_bytes) {
      setStorageOver(true);
    } else {
      setStorageOver(false);
    }
  };

  const navigate = useNavigate();

  const onDrop = useCallback((acceptedFiles) => {
    setSelectedFiles((prevFiles) => {
      const newFiles = [...prevFiles];
      acceptedFiles.forEach((file) => {
        const isDuplicate = newFiles.some((f) => f.name === file.name && f.size === file.size);
        if (!isDuplicate) newFiles.push(file);
      });
      return newFiles;
    });
    setUploadProgress(0);
    setTotalUploaded(0);
    setIsUploading(false);
    setAlertMessage({success: false, message: ""});
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
    multiple: true,
  });

  const uploadFile = async (file, uploadId) => {
    if (abortUpload) return;
    const controller = controllerRef.current;
    const formData = new FormData();
    formData.append("images", file);
    const fileKey = `${file.name}_${file.size}`;
    try {
      await api.post(
        `/images?eid=${event.event_id}&upload_id=${uploadId}&new_size=${newSize}&new_quality=${newQuality}`,
        formData,
        {
          signal: controller.signal,
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            const pct = Math.round((e.loaded / e.total) * 100);
            setFileProgress(prev => ({ ...prev, [fileKey]: pct }));
            const delta = e.loaded - (perFileLoadedRef.current[fileKey] || 0);
            perFileLoadedRef.current[fileKey] = e.loaded;
            totalLoadedRef.current += delta;
            const elapsed = (Date.now() - uploadStartRef.current) / 1000;
            if (elapsed > 0.5) setNetworkSpeed(totalLoadedRef.current / elapsed);
          },
        }
      );
      setFileProgress(prev => ({ ...prev, [fileKey]: 100 }));
      setTotalUploaded((prev) => {
        const newVal = prev + 1;
        totalUploadedRef.current = newVal;
        return newVal;
      });
    } catch (error) {
      const message = error.response?.data?.detail || error.message || "Unknown error";
      if (error.name === "CanceledError") return;
      if (message.includes("Memory limit exceeded")) {
        setAbortUpload(true);
        controller.abort();
        throw new Error("abort-upload");
      }
      throw error;
    }
  };

  const triggerImgProcessing = async () => {
    try {
      const response = await api.get(`/reload_known?eid=${event.event_id}`);
      console.log("Uploaded images processed", response.data);
    } catch (error) {
      console.error(`Error while processing the face on files:`, error);
    }
  };

  const handleUpload = () => {
    setAbortUpload(false);
    setAlertMessage({ success: false, message: "" });
    if (selectedFiles.length === 0) {
      alert("Please select at least one image to upload.");
      return;
    }
    controllerRef.current = new AbortController();
    const uploadId = `upload_${Date.now()}`;
    const limit = pLimit(3);
    uploadStartRef.current = Date.now();
    perFileLoadedRef.current = {};
    totalLoadedRef.current = 0;
    setFileProgress({});
    setNetworkSpeed(null);
    setIsUploading(true);
    const uploadPromises = Array.from(selectedFiles).map((file) => limit(() => uploadFile(file, uploadId)));
    Promise.all(uploadPromises)
      .then(async () => {
        if (!abortUpload) {
          ReactGA.event({ category: 'photographer', action: 'photo_upload', label: event?.name });
          setAlertMessage({ success: true, message: `${selectedFiles.length} images uploaded successfully.` });
          setSelectedFiles([]);
          await triggerImgProcessing();
          await refreshEvents();
        }
      })
      .catch((err) => {
        if (err.message === "abort-upload") {
          let msg = `Out of memory.<br>`;
          if (totalUploadedRef.current > 0) msg += `<strong>${totalUploadedRef.current}</strong> image(s) uploaded before stopping.`;
          setAlertMessage({ success: false, message: msg });
        } else {
          setAlertMessage({ success: false, message: "Upload failed." });
        }
      })
      .finally(() => { setIsUploading(false); setNetworkSpeed(null); });
  };

  const handleRedirect = (path) => path ? () => navigate(`/${path}/${event.id_name}`) : () => navigate(`/${event.id_name}`);

  const memoryLimitBytes = user?.subscription?.memory_limit_gb * 1024 * 1024 * 1024 || 1;
  const availableBytes = Math.max(memoryLimitBytes - usedStorage, 0);
  const usedPct = Math.min((usedStorage / memoryLimitBytes) * 100, 100);

  return (
    <>
      <EventTabNav onBack={() => navigate("/admin/events")} />
    <div style={{ padding: "16px 24px", width: "100%" }}>

      {/* Dropzone */}
      <div
        {...getRootProps()}
        style={{
          border: `2px dashed ${isDragActive ? "#6366f1" : "#c7d2fe"}`,
          borderRadius: "14px",
          background: isDragActive ? "#eef2ff" : "#fafbff",
          cursor: "pointer",
          height: "180px",
          display: "flex", flexDirection: "column",
          justifyContent: "center", alignItems: "center", gap: "10px",
          transition: "all 0.2s",
          marginBottom: "16px",
        }}
      >
        <input {...getInputProps()} />
        <div style={{ width: 44, height: 44, borderRadius: "12px", background: isDragActive ? "#c7d2fe" : "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <FontAwesomeIcon icon={faCamera} style={{ color: "#6366f1", fontSize: "18px" }} />
        </div>
        <div style={{ textAlign: "center" }}>
          <div style={{ fontSize: "13px", fontWeight: 600, color: isDragActive ? "#4338ca" : "#374151" }}>
            {isDragActive ? "Drop your images here" : "Drag & drop images here"}
          </div>
          <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: "3px" }}>PNG, JPEG, JPG · Click to browse</div>
        </div>
      </div>

      {selectedFiles.length > 0 && (
        <>
          {/* Actions bar */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px", gap: "8px" }}>
            <div>
              {isUploading ? (
                <div style={{ display: "flex", alignItems: "center", gap: "7px", color: "#6366f1", fontSize: "13px", fontWeight: 600 }}>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  <span>Uploading…</span>
                </div>
              ) : storageOver ? (
                <div style={{ fontSize: "12px", color: "#ef4444", fontWeight: 600 }}>Storage limit exceeded</div>
              ) : uploadProgress === 0 ? (
                <button
                  onClick={handleUpload}
                  style={{
                    display: "flex", alignItems: "center", gap: "7px",
                    padding: "8px 16px", borderRadius: "9px",
                    border: "none", background: "#6366f1", color: "#fff",
                    fontSize: "12px", fontWeight: 600, cursor: "pointer",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = "#4f46e5"}
                  onMouseLeave={e => e.currentTarget.style.background = "#6366f1"}
                >
                  <FontAwesomeIcon icon={faUpload} style={{ fontSize: "11px" }} />
                  Upload
                </button>
              ) : null}
            </div>

            {!isUploading && (
              <button
                onClick={() => setShowManager(!showManager)}
                style={{
                  display: "flex", alignItems: "center", gap: "6px",
                  padding: "7px 12px", borderRadius: "8px",
                  border: "1px solid #e5e7eb", background: "#fff",
                  color: "#6b7280", cursor: "pointer",
                  fontSize: "12px", fontWeight: 500,
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f9fafb"}
                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
              >
                <FontAwesomeIcon icon={faSlidersH} style={{ fontSize: "11px" }} />
                Size / Quality
                <FontAwesomeIcon icon={showManager ? faChevronUp : faChevronDown} style={{ fontSize: "10px" }} />
              </button>
            )}
          </div>

          {/* Size/Quality manager */}
          {showManager && !isUploading && (
            <div style={{
              padding: "14px 16px", borderRadius: "12px",
              border: "1px solid #e5e7eb", background: "#fafafa",
              marginBottom: "12px",
            }}>
              <div style={{ marginBottom: "12px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <Form.Check
                    id="originalSizeCheckbox" type="checkbox"
                    checked={newSize === -1}
                    onChange={() => setNewSize(newSize === -1 ? 1600 : -1)}
                    style={{ cursor: "pointer" }}
                  />
                  <label htmlFor="originalSizeCheckbox" style={{ fontSize: "12px", fontWeight: 600, color: "#374151", cursor: "pointer", marginBottom: 0 }}>
                    Original Size
                  </label>
                </div>
                {newSize !== -1 && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                      <span style={{ fontSize: "11px", color: "#6b7280" }}>New size</span>
                      <span style={{ fontSize: "11px", fontWeight: 700, color: "#374151" }}>{newSize}px</span>
                    </div>
                    <Form.Range min={1000} max={4000} step={100} value={newSize}
                      onChange={(e) => setNewSize(Number(e.target.value))}
                      onMouseUp={handleEstimateSize} onTouchEnd={handleEstimateSize}
                    />
                  </div>
                )}
              </div>
              <div>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                  <span style={{ fontSize: "11px", color: "#6b7280" }}>Quality</span>
                  <span style={{ fontSize: "11px", fontWeight: 700, color: "#374151" }}>{newQuality}%</span>
                </div>
                <Form.Range min={50} max={100} step={1} value={newQuality}
                  onChange={(e) => setNewQuality(Number(e.target.value))}
                  onMouseUp={handleEstimateSize} onTouchEnd={handleEstimateSize}
                />
              </div>
            </div>
          )}

          {/* Storage + size info */}
          <div style={{
            padding: "10px 14px", borderRadius: "10px",
            border: "1px solid #f0f0f0", background: "#fafafa",
            marginBottom: "10px",
          }}>
            {/* Storage bar */}
            <div style={{ marginBottom: "8px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "4px" }}>
                <span style={{ fontSize: "11px", color: "#9ca3af" }}>Storage used</span>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#374151" }}>{bytesToSize(availableBytes)} available</span>
              </div>
              <div style={{ height: 4, borderRadius: 4, background: "#e5e7eb" }}>
                <div style={{ height: "100%", borderRadius: 4, background: usedPct > 85 ? "#ef4444" : "#6366f1", width: `${usedPct}%`, transition: "width 0.3s" }} />
              </div>
            </div>

            {/* File size estimate */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: "#6b7280" }}>
                {estimating ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "11px" }} />
                    <span>Estimating…</span>
                  </>
                ) : (
                  totalSize !== previewSize ? (
                    <span>{totalSize} <span style={{ color: "#6366f1" }}>→</span> <strong style={{ color: "#374151" }}>{previewSize}</strong></span>
                  ) : (
                    <strong style={{ color: "#374151" }}>{previewSize}</strong>
                  )
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#374151" }}>
                  {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}
                </span>
                {!isUploading && (
                  <button
                    title="Remove all"
                    onClick={() => setSelectedFiles([])}
                    style={{
                      width: 22, height: 22, borderRadius: "50%",
                      border: "1px solid #fca5a5", background: "#fff5f5",
                      color: "#ef4444", cursor: "pointer",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: "10px",
                    }}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Progress bar */}
          {isUploading && (
            <div style={{ marginBottom: "12px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                <span style={{ fontSize: "11px", color: "#9ca3af", display: "flex", alignItems: "center", gap: "8px" }}>
                  {totalUploaded} / {selectedFiles.length} files
                  {networkSpeed && (
                    <span style={{ color: "#6366f1", fontWeight: 600 }}>↑ {formatSpeed(networkSpeed)}</span>
                  )}
                </span>
                <span style={{ fontSize: "12px", fontWeight: 700, color: "#6366f1" }}>
                  {uploadProgress}%
                </span>
              </div>
              <div style={{ height: 5, borderRadius: 99, background: "#e5e7eb", overflow: "hidden" }}>
                <div style={{
                  height: "100%",
                  borderRadius: 99,
                  background: "linear-gradient(90deg, #6366f1, #a5b4fc)",
                  width: `${uploadProgress}%`,
                  transition: "width 0.35s ease",
                }} />
              </div>
            </div>
          )}

          {/* File list */}
          <div style={{ borderRadius: "12px", border: "1px solid #f0f0f0", overflow: "hidden" }}>
            <div style={{ maxHeight: "220px", overflowY: "auto" }}>
              {Array.from(selectedFiles).map((file, index) => {
                const fileKey = `${file.name}_${file.size}`;
                const pct = fileProgress[fileKey] ?? 0;
                const done = pct === 100;
                return (
                  <div
                    key={index}
                    style={{
                      display: "flex", flexDirection: "column",
                      borderBottom: index < selectedFiles.length - 1 ? "1px solid #f3f4f6" : "none",
                      background: "#fff",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}>
                        <div style={{
                          width: 28, height: 28, borderRadius: "7px",
                          background: isUploading && done ? "#f0fdf4" : "#eef2ff",
                          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                          transition: "background 0.3s",
                        }}>
                          <FontAwesomeIcon icon={faCamera} style={{ color: isUploading && done ? "#10b981" : "#6366f1", fontSize: "11px", transition: "color 0.3s" }} />
                        </div>
                        <span style={{ fontSize: "12px", color: "#374151", fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }}>
                          {file.name}
                        </span>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                        {isUploading ? (
                          <span style={{ fontSize: "11px", fontWeight: 700, color: done ? "#10b981" : "#6366f1", minWidth: "32px", textAlign: "right" }}>
                            {pct}%
                          </span>
                        ) : (
                          <span style={{ fontSize: "11px", color: "#9ca3af" }}>{bytesToSize(file.size)}</span>
                        )}
                        {!isUploading && (
                          <button
                            title="Remove"
                            onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== index))}
                            style={{
                              width: 20, height: 20, borderRadius: "50%",
                              border: "1px solid #fca5a5", background: "#fff5f5",
                              color: "#ef4444", cursor: "pointer",
                              display: "flex", alignItems: "center", justifyContent: "center",
                              fontSize: "9px",
                            }}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </button>
                        )}
                      </div>
                    </div>
                    {isUploading && (
                      <div style={{ height: 2, background: "#f3f4f6" }}>
                        <div style={{
                          height: "100%",
                          background: done ? "#10b981" : "#6366f1",
                          width: `${pct}%`,
                          transition: "width 0.2s ease, background 0.3s",
                        }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Alert */}
      {alertMessage.message && (
        <div
          style={{
            display: "flex", alignItems: "center", gap: "10px",
            marginTop: "14px", padding: "12px 16px", borderRadius: "10px",
            border: `1px solid ${alertMessage.success ? "#bbf7d0" : "#fecaca"}`,
            background: alertMessage.success ? "#f0fdf4" : "#fff5f5",
            fontSize: "13px", color: alertMessage.success ? "#166534" : "#991b1b",
          }}
        >
          <FontAwesomeIcon icon={alertMessage.success ? faCheckCircle : faTimesCircle} style={{ flexShrink: 0, fontSize: "14px" }} />
          <span dangerouslySetInnerHTML={{ __html: alertMessage.message }} />
        </div>
      )}
    </div>
    </>
  );
};

export default UploadImg;
